/**
 * Pure serializer: Firestore panel document → ESP32 deployable payload.
 * Phase 4 — no transport.
 */

import {
  PANEL_SCHEMA_VERSION,
  PANEL_ACTION_OPEN_SCREEN,
  PANEL_ACTION_FLOW,
  PANEL_ACTION_NONE,
  DEFAULT_PANEL_BG,
} from '../../pages/Admin/TouchPanelDesigner/panelModel';

function buildAction(button) {
  const type = button?.actionType || PANEL_ACTION_NONE;
  if (type === PANEL_ACTION_OPEN_SCREEN) {
    return {
      type,
      config: { targetScreenId: button.targetScreenId || null },
    };
  }
  if (type === PANEL_ACTION_FLOW) {
    return {
      type,
      config: { flowId: button.flowId || null },
    };
  }
  return { type, config: {} };
}

function buildVisual(button) {
  return {
    iconType: button.iconType || 'builtin',
    iconKey: button.iconKey ?? null,
    iconText: button.iconText ?? null,
    iconBackgroundColor: button.iconBg ?? button.iconBackgroundColor ?? null,
    buttonBackgroundColor: button.buttonBackgroundColor ?? null,
    textColor: button.textColor ?? null,
    iconColor: button.iconColor ?? null,
  };
}

function serializeButton(button) {
  return {
    id: button.id,
    label: button.label || '',
    position: Number(button.position),
    visual: buildVisual(button),
    action: buildAction(button),
  };
}

function serializeScreen(screen) {
  return {
    id: screen.id,
    name: screen.name || '',
    rows: Number(screen.rows) || 1,
    columns: Number(screen.columns) || 1,
    appearance: {
      backgroundMode: screen.appearance?.backgroundMode || 'inherit',
      backgroundColor: screen.appearance?.backgroundColor ?? null,
    },
    buttons: (screen.buttons || []).map(serializeButton),
  };
}

/**
 * Convert a saved panel document into the deployable ESP32 payload.
 * Strips Firestore/editor-only fields.
 */
export function serializeDeployablePanel(panel) {
  const schemaVersion =
    Number(panel?.schemaVersion) > 0 ? Number(panel.schemaVersion) : PANEL_SCHEMA_VERSION;

  return {
    schemaVersion,
    panel: {
      id: panel.id,
      name: panel.name || '',
      revision: Number(panel.revision) > 0 ? Number(panel.revision) : 1,
      defaultScreenId: panel.defaultScreenId || null,
      branding: {
        logoUrl: panel.branding?.logoUrl ?? null,
      },
      appearance: {
        backgroundColor: panel.appearance?.backgroundColor || DEFAULT_PANEL_BG,
      },
      screens: (panel.screens || []).map(serializeScreen),
    },
  };
}

/** UTF-8 byte size of JSON payload. */
export function measurePayloadBytes(payload) {
  const json = typeof payload === 'string' ? payload : JSON.stringify(payload);
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(json).length;
  }
  return Buffer.byteLength(json, 'utf8');
}

/** Aggregate size / resource estimates for Prepare Deployment UI. */
export function summarizePayload(payload) {
  const screens = payload?.panel?.screens || [];
  const buttonCounts = screens.map((s) => (s.buttons || []).length);
  const totalButtons = buttonCounts.reduce((a, b) => a + b, 0);
  const maxButtonsOnScreen = buttonCounts.length ? Math.max(...buttonCounts) : 0;

  let estimatedIconDependencies = 0;
  screens.forEach((s) => {
    (s.buttons || []).forEach((b) => {
      if (b.visual?.iconType === 'builtin' && b.visual?.iconKey) estimatedIconDependencies += 1;
      if (b.visual?.iconType === 'text') estimatedIconDependencies += 1;
    });
  });

  return {
    sizeBytes: measurePayloadBytes(payload),
    screenCount: screens.length,
    buttonCount: totalButtons,
    maxButtonsOnScreen,
    estimatedIconDependencies,
    hasLogo: !!payload?.panel?.branding?.logoUrl,
  };
}
